import { useState } from 'react';
import { PiDotsThreeVerticalBold, PiFolderOpenBold, PiUserCircleBold } from 'react-icons/pi';
import { cn } from '@v2/utils/cn';
import { roleAccent } from './role-colors';

export type RoleCardData = {
    id: number;
    name: string;
    label: string;
    users_count: number;
    is_protected: boolean;
    color: string;
    users_preview: Array<{ id: number; name: string; avatar_url: string | null }>;
};

type Props = {
    role: RoleCardData;
    onEdit: () => void;
    onDelete?: () => void;
};

const FALLBACK = '/img/favicon.png';

export default function RoleCard({ role, onEdit, onDelete }: Props) {
    const [menuOpen, setMenuOpen] = useState(false);
    const accent = roleAccent(role.color);

    return (
        <article className="flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition hover:shadow-md dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-center gap-3">
                    <span
                        className={cn(
                            'flex size-10 shrink-0 items-center justify-center rounded-xl text-white shadow-sm',
                            accent.icon,
                        )}
                    >
                        <PiFolderOpenBold className="size-5" />
                    </span>
                    <div className="min-w-0">
                        <h3 className="truncate text-base font-bold text-gray-900 dark:text-gray-100">{role.label}</h3>
                        <p className="truncate font-mono text-[10px] text-gray-400 dark:text-gray-500">{role.name}</p>
                    </div>
                </div>
                {!role.is_protected && onDelete && (
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => setMenuOpen((v) => !v)}
                            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                            aria-label="Opções"
                        >
                            <PiDotsThreeVerticalBold className="size-5" />
                        </button>
                        {menuOpen && (
                            <>
                                <button
                                    type="button"
                                    className="fixed inset-0 z-10"
                                    aria-label="Fechar menu"
                                    onClick={() => setMenuOpen(false)}
                                />
                                <div className="absolute right-0 z-20 mt-1 w-36 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                    <button
                                        type="button"
                                        className="block w-full px-3 py-2 text-left text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                                        onClick={() => {
                                            setMenuOpen(false);
                                            onDelete();
                                        }}
                                    >
                                        Excluir função
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="mt-5 flex items-center gap-3">
                <div className="flex -space-x-2">
                    {role.users_preview.length === 0 && (
                        <span className="inline-flex size-8 items-center justify-center rounded-full border-2 border-white bg-gray-100 text-gray-400 dark:border-gray-800 dark:bg-gray-700 dark:text-gray-500">
                            <PiUserCircleBold className="size-5" />
                        </span>
                    )}
                    {role.users_preview.map((user) => (
                        <img
                            key={user.id}
                            src={user.avatar_url || FALLBACK}
                            alt={user.name}
                            title={user.name}
                            className="size-8 rounded-full border-2 border-white bg-gray-100 object-cover dark:border-gray-800"
                            onError={(e) => {
                                (e.target as HTMLImageElement).src = FALLBACK;
                            }}
                        />
                    ))}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    Total <span className="font-semibold text-gray-900 dark:text-gray-100">{role.users_count}</span> usuário
                    {role.users_count === 1 ? '' : 's'}
                </p>
            </div>

            <button
                type="button"
                onClick={onEdit}
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-50 py-2.5 text-sm font-semibold text-gray-800 transition hover:border-gray-300 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-700/40 dark:text-gray-200 dark:hover:border-gray-600 dark:hover:bg-gray-700"
            >
                <PiUserCircleBold className="size-4" />
                Editar função
            </button>
        </article>
    );
}
